/**
 * Affiliate attribution (Phase 2) — capture aff_* from landing URLs and
 * persist onto Sharetribe transaction metadata via Integration API.
 *
 * Mirrors the GA4 attribution pattern (session → authenticated API → updateMetadata).
 * Commission rates are never taken from the URL; the server resolves rate + amount.
 *
 * @see server/api/affiliateRecordAttribution.js
 * @see docs in Marketplace Affiliate Plugin: docs/staging-teak-handoff.md
 */

const SESSION_KEY = 'promotix_affiliate_attribution';
const ALLOWED_CHANNELS = new Set(['widget', 'site', 'link', 'email', 'api']);

const safeSessionGet = key => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // ignore quota / private mode
  }
};

const sanitizeId = (raw, maxLen = 64) => {
  if (raw == null) return '';
  const s = String(raw).trim().slice(0, maxLen);
  // Affiliate plugin slugs: lowercase alphanumerics and hyphens
  if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/.test(s)) return '';
  return s;
};

const sanitizeChannel = raw => {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  return ALLOWED_CHANNELS.has(s) ? s : '';
};

/**
 * Parse aff_* (+ optional utm_*) from a location.search string or URLSearchParams.
 * @returns {object|null}
 */
export const parseAffiliateParamsFromSearch = search => {
  if (search == null || search === '') return null;
  try {
    const q =
      typeof search === 'string'
        ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
        : search;
    const aff_id = sanitizeId(q.get('aff_id'));
    if (!aff_id) return null;
    const aff_campaign = sanitizeId(q.get('aff_campaign') || '', 80) || undefined;
    const aff_channel = sanitizeChannel(q.get('aff_channel')) || undefined;
    const aff_sub = sanitizeId(q.get('aff_sub') || '', 64) || undefined;
    const utm_source = sanitizeId(q.get('utm_source') || '', 80) || undefined;
    const utm_medium = sanitizeId(q.get('utm_medium') || '', 40) || undefined;
    const utm_campaign = sanitizeId(q.get('utm_campaign') || '', 80) || undefined;
    return {
      aff_id,
      ...(aff_campaign ? { aff_campaign } : {}),
      ...(aff_channel ? { aff_channel } : {}),
      ...(aff_sub ? { aff_sub } : {}),
      ...(utm_source ? { utm_source } : {}),
      ...(utm_medium ? { utm_medium } : {}),
      ...(utm_campaign ? { utm_campaign } : {}),
    };
  } catch (e) {
    return null;
  }
};

/**
 * Last-click: when aff_id is present on the current URL, overwrite session attribution.
 * Call on every SPA navigation (Routes) — checkout drops query params.
 */
export const captureAffiliateAttributionFromLocation = location => {
  if (typeof window === 'undefined' || !location) return null;
  const parsed = parseAffiliateParamsFromSearch(location.search);
  if (!parsed?.aff_id) return null;
  const payload = {
    ...parsed,
    capturedAt: new Date().toISOString(),
    landingPath: `${location.pathname || ''}${location.search || ''}`,
  };
  try {
    safeSessionSet(SESSION_KEY, JSON.stringify(payload));
  } catch (e) {
    // ignore
  }
  return payload;
};

export const readAffiliateAttribution = () => {
  const raw = safeSessionGet(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.aff_id) return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

/**
 * Persist affiliate attribution on transaction metadata (server Integration API).
 * Fire-and-forget safe; never throws to callers.
 *
 * @param {string|object} transactionId
 * @param {object} [listing] — optional listing for listingType hint (server re-resolves)
 */
export const persistAffiliateAttributionForTransaction = async (transactionId, listing) => {
  const txId =
    typeof transactionId === 'string'
      ? transactionId
      : transactionId?.uuid || transactionId?.id?.uuid || null;
  if (!txId) return { ok: false, reason: 'no_transaction_id' };

  const attr = readAffiliateAttribution();
  if (!attr?.aff_id) {
    return { ok: false, reason: 'no_affiliate_session' };
  }

  const publicData = listing?.attributes?.publicData || listing?.publicData || null;

  try {
    const { recordAffiliateAttributionMetadata } = await import('./api');
    await recordAffiliateAttributionMetadata({
      transactionId: txId,
      aff_id: attr.aff_id,
      aff_campaign: attr.aff_campaign,
      aff_channel: attr.aff_channel,
      aff_sub: attr.aff_sub,
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      capturedAt: attr.capturedAt || new Date().toISOString(),
      landingPath: attr.landingPath,
      // Hint only — server validates listingType from transaction/listing
      listingPublicData: publicData || undefined,
    });
    return { ok: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[affiliate] attribution persist failed', e);
    return { ok: false, reason: 'api_error' };
  }
};
